'use client'

import { ChevronsRight, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Slider-puzzle captcha, ported from `oasis_website_php/assets/v3/Captcha/slidercaptcha.js`.
 * Geometry, tolerance and pass criteria reproduced exactly, so difficulty is unchanged.
 * Client-side only — a UI deterrent, not access control. Anything POSTing straight at Laravel
 * bypasses it; the real control is the rate limit in `verifyGift`.
 * Self-contained: no redeem state, no network.
 */

const WIDTH = 280
const HEIGHT = 160
const SLIDER_L = 42 // puzzle side
const SLIDER_R = 9 // tab radius
const OFFSET = 8 // drop tolerance in px
const HANDLE_W = 40 // slider handle width, hardcoded in the legacy drag math
const BLOCK_L = SLIDER_L + SLIDER_R * 2 + 3 // 63 — real block side
const IMAGE_COUNT = 8 // public/static/images/captcha/{0-7}.jpg
const SUCCESS_HOLD_MS = 600
const FAIL_HOLD_MS = 1800

type Status = 'loading' | 'idle' | 'dragging' | 'success' | 'fail' | 'error'

interface Props {
  onSuccess: () => void
  onFail?: () => void
  /** Localised strings. Defaults keep the component usable standalone. */
  labels?: { bar?: string; failed?: string; success?: string; loading?: string; refresh?: string }
  /** Fixed background index. Omit for a random one, as the legacy page does. */
  imageIndex?: number
}

export default function SliderCaptcha({ onSuccess, onFail, labels, imageIndex }: Props) {
  const bgRef = useRef<HTMLCanvasElement>(null)
  const blockRef = useRef<HTMLCanvasElement>(null)

  /** Puzzle hole centre. Refs, not state — the drag handlers read it every pointermove. */
  const targetX = useRef(0)
  const blockLeft = useRef(0)
  const trail = useRef<number[]>([])

  /** The one pending hold timer, so unmounting mid-hold cannot fire into a dead component. */
  const holdTimer = useRef<number>(0)

  const [status, setStatus] = useState<Status>('loading')
  const [barText, setBarText] = useState(labels?.bar ?? 'Slide the piece into place')
  const [sliderLeft, setSliderLeft] = useState(0)
  const [maskWidth, setMaskWidth] = useState(0)
  const [seed, setSeed] = useState(0)

  const barLabel = labels?.bar ?? 'Slide the piece into place'
  const failedLabel = labels?.failed ?? 'Not quite — try again'
  const successLabel = labels?.success ?? 'Verified'

  useEffect(() => () => window.clearTimeout(holdTimer.current), [])

  /** Puzzle outline: square with a tab on top and right, and a bite out of the left. */
  const drawPiece = useCallback(
    (ctx: CanvasRenderingContext2D, operation: 'fill' | 'clip', x: number, y: number) => {
      const l = SLIDER_L
      const r = SLIDER_R
      const PI = Math.PI

      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.arc(x + l / 2, y - r + 2, r, 0.72 * PI, 2.26 * PI)
      ctx.lineTo(x + l, y)
      ctx.arc(x + l + r - 2, y + l / 2, r, 1.21 * PI, 2.78 * PI)
      ctx.lineTo(x + l, y + l)
      ctx.lineTo(x, y + l)
      ctx.arc(x + r - 2, y + l / 2, r + 0.4, 2.76 * PI, 1.24 * PI, true)
      ctx.lineTo(x, y)
      ctx.lineWidth = 2
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)'
      ctx.stroke()
      ctx[operation]()
      ctx.globalCompositeOperation = 'destination-over'
    },
    []
  )

  // Build the puzzle. Re-runs on refresh (`seed`) to get a new image and hole position.
  useEffect(() => {
    const bg = bgRef.current
    const block = blockRef.current
    if (!bg || !block) return

    const bgCtx = bg.getContext('2d')
    const blockCtx = block.getContext('2d')
    if (!bgCtx || !blockCtx) return

    setStatus('loading')

    const index = imageIndex ?? Math.floor(Math.random() * IMAGE_COUNT)
    const img = new Image()
    let cancelled = false

    img.onload = () => {
      if (cancelled) return

      const rand = (start: number, end: number) => Math.round(Math.random() * (end - start) + start)

      const x = rand(BLOCK_L + 10, WIDTH - (BLOCK_L + 10))
      const y = rand(10 + SLIDER_R * 2, HEIGHT - (BLOCK_L + 10))
      targetX.current = x

      block.width = WIDTH

      bgCtx.clearRect(0, 0, WIDTH, HEIGHT)

      drawPiece(bgCtx, 'fill', x, y)
      drawPiece(blockCtx, 'clip', x, y)

      bgCtx.drawImage(img, 0, 0, WIDTH - 2, HEIGHT)
      blockCtx.drawImage(img, 0, 0, WIDTH - 2, HEIGHT)

      // Lift the clipped piece out, then narrow the block canvas to it. Setting `width` clears
      // the canvas, so read the pixels first and write them back after.
      const sourceY = y - SLIDER_R * 2 - 1
      const piece = blockCtx.getImageData(x - 3, sourceY, BLOCK_L, BLOCK_L)
      block.width = BLOCK_L
      blockCtx.putImageData(piece, 0, sourceY + 1)

      setStatus('idle')
      setBarText(barLabel)
    }

    img.onerror = () => {
      if (!cancelled) setStatus('error')
    }

    // Same-origin, so reading pixels back out does not taint the canvas.
    img.src = `/static/images/captcha/${index}.jpg`

    return () => {
      cancelled = true
    }
  }, [seed, imageIndex, drawPiece, barLabel])

  const reset = useCallback(() => {
    window.clearTimeout(holdTimer.current)
    setSliderLeft(0)
    setMaskWidth(0)
    blockLeft.current = 0
    trail.current = []
    if (blockRef.current) blockRef.current.style.left = '0px'
  }, [])

  const refresh = useCallback(() => {
    reset()
    setSeed((n) => n + 1)
  }, [reset])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (status !== 'idle') return

      const originX = event.clientX
      const originY = event.clientY
      trail.current = []
      setStatus('dragging')

      const onMove = (move: PointerEvent) => {
        const moveX = move.clientX - originX
        if (moveX < 0 || moveX + HANDLE_W > WIDTH) return

        setSliderLeft(moveX - 1)
        setMaskWidth(moveX + 4)

        // Scales the travel so the piece lands on the hole as the handle bottoms out.
        // Legacy formula, verbatim.
        const left = ((WIDTH - HANDLE_W - 20) / (WIDTH - HANDLE_W)) * moveX
        blockLeft.current = left
        if (blockRef.current) blockRef.current.style.left = `${left}px`

        trail.current.push(Math.round(move.clientY - originY))
      }

      const onUp = (up: PointerEvent) => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)

        if (up.clientX === originX) {
          setStatus('idle')
          return
        }

        const spliced = Math.abs(blockLeft.current - targetX.current) < OFFSET
        // Legacy human-check: a drag with zero vertical variance reads as scripted.
        const human = standardDeviation(trail.current) !== 0

        if (spliced && human) {
          setStatus('success')
          setBarText(successLabel)
          holdTimer.current = window.setTimeout(onSuccess, SUCCESS_HOLD_MS)
          return
        }

        setStatus('fail')
        onFail?.()
        setBarText(failedLabel)
        holdTimer.current = window.setTimeout(() => {
          reset()
          setStatus('idle')
          setBarText(barLabel)
        }, FAIL_HOLD_MS)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    [status, onSuccess, onFail, reset, barLabel, failedLabel, successLabel]
  )

  const trackTone =
    status === 'success'
      ? 'border-green-400 bg-green-50'
      : status === 'fail'
        ? 'border-red-400 bg-red-50'
        : 'border-gray-200 bg-gray-100'

  const textTone =
    status === 'success'
      ? 'font-bold text-green-700'
      : status === 'fail'
        ? 'font-bold text-red-700'
        : 'font-medium text-gray-600'

  return (
    <div className='mx-auto select-none' style={{ width: WIDTH }}>
      <div className='relative overflow-hidden rounded-lg bg-gray-100' style={{ height: HEIGHT }}>
        <canvas ref={bgRef} width={WIDTH} height={HEIGHT} className='block' />
        <canvas
          ref={blockRef}
          width={WIDTH}
          height={HEIGHT}
          className='absolute top-0 cursor-grab'
          style={{ left: 0 }}
        />

        {status === 'loading' && (
          <div className='absolute inset-0 grid place-items-center bg-gray-100 text-xs text-gray-500'>
            {labels?.loading ?? 'Loading…'}
          </div>
        )}

        {status === 'error' && (
          <div className='absolute inset-0 grid place-items-center gap-2 bg-gray-100 px-4 text-center text-xs text-gray-600'>
            <span>Could not load the puzzle image.</span>
            <button type='button' onClick={refresh} className='font-bold text-theme underline'>
              {labels?.refresh ?? 'Try again'}
            </button>
          </div>
        )}

        <button
          type='button'
          onClick={refresh}
          aria-label={labels?.refresh ?? 'New puzzle'}
          className='absolute right-2 top-2 rounded-full bg-white/80 p-1.5 text-gray-600 shadow transition-colors hover:bg-white hover:text-theme'>
          <RotateCcw className='h-3.5 w-3.5' />
        </button>
      </div>

      <div
        className={`relative mt-3 flex items-center overflow-hidden rounded-full border transition-colors ${trackTone}`}
        style={{ height: HANDLE_W }}>
        <div
          className={`absolute left-0 top-0 h-full ${status === 'fail' ? 'bg-red-200' : 'bg-theme/20'
            }`}
          style={{ width: maskWidth }}
        />
        {/* `z-10` is load-bearing: the handle is a later positioned sibling, so without it the
            white handle paints over the message exactly where the failed drag stopped. */}
        <span
          className={`pointer-events-none relative z-10 w-full text-center text-xs ${textTone}`}>
          {barText}
        </span>
        <div
          role='slider'
          tabIndex={-1}
          aria-label={barLabel}
          aria-valuenow={Math.round(sliderLeft)}
          aria-valuemin={0}
          aria-valuemax={WIDTH - HANDLE_W}
          onPointerDown={handlePointerDown}
          className={`absolute top-0 grid h-full place-items-center rounded-full bg-white shadow-md ${status === 'idle' || status === 'dragging' ? 'cursor-grab' : 'cursor-default'
            }`}
          style={{ width: HANDLE_W, left: sliderLeft, touchAction: 'none' }}>
          <ChevronsRight className='h-4 w-4 text-gray-500' />
        </div>
      </div>
    </div>
  )
}

function standardDeviation(values: number[]): number {
  if (!values.length) return 0

  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length

  return Math.sqrt(variance)
}
